import { GrcNotify, NotifyType, PagedResult } from '@grc/ui-package';
import { quoteService } from '../../common/constants/constants';
import {
  fetchQuoteSettings,
  quotesV1Url
} from '../../common/constants/url-constants';
import {
  IQuote,
  IQuoteApprover,
  QuoteStatus
} from '../../models/quotes/quote.interface';

/**
 * A page of GET /v1/quotes that did not load. `notified`: fetchData already
 * toasted it (400/409). `cancelled`: the request was aborted (fetchData
 * resolves null without notifying). Either way the grid's onError stays quiet.
 * The message is for logs only; the user sees a localized text.
 */
export class QuotesPageError extends Error {
  readonly notified: boolean;
  readonly cancelled: boolean;

  constructor(message: string, notified: boolean, cancelled: boolean) {
    super(message);
    this.name = 'QuotesPageError';
    this.notified = notified;
    this.cancelled = cancelled;
  }
}

/** The parts of useGetApiData().fetchData that GetQuotesPage uses. */
export type QuotesPageFetchData = (
  url: string,
  notify: (message: string) => void,
  baseService: string,
  params: URLSearchParams
) => Promise<{ status: number; data: Partial<PagedResult<IQuote>> } | null>;

// One page of GET /v1/quotes (TmsPagedList). Throws when the page did not
// load so the grid marks it failed; other errors from fetchData propagate.
export const GetQuotesPage = async (
  fetchData: QuotesPageFetchData,
  params: URLSearchParams
): Promise<PagedResult<IQuote>> => {
  let notified = false;
  const response = await fetchData(
    quotesV1Url,
    (message: string) => {
      notified = true;
      GrcNotify({ message, status: NotifyType.Error });
    },
    quoteService,
    params
  );
  if (response?.status !== 200) {
    // fetchData resolves null in two cases only: after handleApiError toasted a
    // 400/409 (notify was called), or for a cancelled request (it was not).
    // Every other failure is rethrown by fetchData and propagates from here.
    throw new QuotesPageError(
      `GET ${quotesV1Url} failed`,
      notified,
      response === null && !notified
    );
  }
  return {
    ...response.data,
    items: response.data?.items ?? [],
    totalItemCount: response.data?.totalItemCount ?? 0
  };
};

export const GetQuoteSettings = async (
  fetchData: any,
  accountId: string
): Promise<any> => {
  try {
    const response = await fetchData(
      fetchQuoteSettings,
      (message: string) => GrcNotify({ message, status: NotifyType.Error }),
      quoteService,
      { ownerId: accountId }
    );
    return response?.data ?? null;
  } catch (error: any) {
    GrcNotify({
      message: error.message,
      status: NotifyType.Error
    });
    return null;
  }
};

// PUT /v1/quotes/{id}/approval - single-quote-per-call, no batch endpoint exists
// (plan §5 Approve/Reject gap 1) - useApprovalDecision loops this per id.
// Assumes CustomerApproval is the track this customer-facing app writes to
// (plan §9 item 9, unconfirmed) - the endpoint doesn't take a discriminator.
export const SubmitQuoteApproval = async (
  putData: any,
  quoteId: string,
  approver: IQuoteApprover
): Promise<boolean> => {
  try {
    const response = await putData(
      `${quotesV1Url}/${quoteId}/approval`,
      (message: string) => GrcNotify({ message, status: NotifyType.Error }),
      quoteService,
      { quoteId, approver }
    );
    return !!response;
  } catch (error: any) {
    GrcNotify({
      message: error.message,
      status: NotifyType.Error
    });
    return false;
  }
};

// PUT /v1/quotes/{id}/update - used only to reject a quote still at the
// `Quote Requests` stage, where no CustomerApproval/InternalApproval object
// necessarily exists yet for /approval to target (plan §5 Approve/Reject gap
// 3). Redefined as "cancel" per the plan's recommendation. `reason` is sent
// defensively the same way Revision 2 sends date-filter params before the
// backend declares them - `UpdateQuote` has no confirmed `Reason` field
// today, so this is a harmless no-op comment until that's added.
export const UpdateQuoteStatus = async (
  putData: any,
  quoteId: string,
  status: QuoteStatus,
  reason?: string
): Promise<boolean> => {
  try {
    const response = await putData(
      `${quotesV1Url}/${quoteId}/update`,
      (message: string) => GrcNotify({ message, status: NotifyType.Error }),
      quoteService,
      { quoteId, status, reason }
    );
    return !!response;
  } catch (error: any) {
    GrcNotify({
      message: error.message,
      status: NotifyType.Error
    });
    return false;
  }
};

export default GetQuotesPage;
